import React, { useRef, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Message } from './Message';
import { QuickActions } from './QuickActions';
import { useChatContext } from './ChatContext';
import InlineLoader from '@/components/InlineLoader';

export const ChatWindow: React.FC = () => {
  const { messages, sendMessage, loading, clearHistory, setChatOpen } = useChatContext();
  const [inputValue, setInputValue] = React.useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // Focus input when chat window opens
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !loading) {
      sendMessage(inputValue);
      setInputValue('');
    }
  };
  
  const handleQuickAction = (question: string) => {
    if (!loading) {
      sendMessage(question);
    }
  };
  
  return (
    <div className="w-full h-full flex flex-col bg-heaven-accent rounded-xl overflow-hidden border border-heaven-gold/20 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between bg-[#34495E] px-4 py-3">
        <div className="flex items-center">
          <img 
            src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//little%20dove%20(1).png"
            alt="Selah" 
            className="h-10 w-10 mr-2 object-contain"
            style={{ background: 'transparent' }}
          />
          <h3 className="text-white font-medium">Selah Assistant</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="p-0 h-8 w-8 rounded-full text-white hover:bg-white/10"
            onClick={clearHistory}
            title="Clear chat"
          >
            <span className="sr-only">Clear</span>
            <span className="text-xs font-medium">Clear</span>
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="p-0 h-8 w-8 rounded-full text-white hover:bg-white/10"
            onClick={() => setChatOpen(false)}
          >
            <span className="sr-only">Close chat</span>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {messages.map(message => (
          <Message key={message.id} message={message} />
        ))}
        
        {loading && (
          <div className="flex justify-start mb-4">
            <div className="bg-white border border-gray-200 p-3 rounded-xl">
              <InlineLoader text="Composing a response..." />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Quick Actions */}
      {messages.length < 3 && (
        <QuickActions onSelect={handleQuickAction} />
      )}
      
      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 border-t border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            type="text"
            placeholder="Type your message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            disabled={loading}
            className="flex-1 border-heaven-gold/20"
            aria-label="Type your message"
          />
          <Button 
            type="submit" 
            disabled={inputValue.trim() === '' || loading}
            className="bg-heaven-gold hover:bg-heaven-dark-gold text-white"
            size="icon"
          >
            <Send className="h-4 w-4" />
            <span className="sr-only">Send message</span>
          </Button>
        </div>
      </form>
    </div>
  );
};
